import { IFaxDocument, IFaxMessage, IFaxTag } from '@dataTypes/fax.type'
import {
  getBlockedPhoneSet,
  getFaxBlockedUsers,
  getFaxFetchView,
  getFaxListViewMode,
  getFaxMessagesForView,
  getFaxViewCapabilities,
  isFaxInboxView,
  formatFaxListDate,
  getFaxCounterpartNumber,
  getFaxDisplayLabel,
  matchesBlockedSearch,
  matchesBlockedFilters,
  matchesFaxFilters,
  FaxListViewMode,
  FaxViewCapabilities,
} from '@utils/fax.util'
import { downloadFile } from '@services/api/fax/document.api'
import {
  bulkDeleteFax,
  bulkUpdateFax,
  deleteFax,
  fetchAllBlockedUsers,
  fetchAllFax,
  unblockUser,
  updateBlockedUser,
  updateFax,
} from '@store/actions'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { setSelectedFaxId, setActiveFolder } from '@store/reducers/fax.reducer'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { IBlockedUser } from '@dataTypes/blocked-user.type'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createFailedToast,
  createSuccessToast,
  createFaxResendingToast,
  createFaxResendFailedToast,
  createFaxMovedToTrashToast,
  createFaxRestoredToInboxToast,
  createFaxMovedToFolderToast,
} from '@utils/notificationToast.util'
import { normalizePhoneNumber } from '@utils/numberFormatter.util'
import { showCommunicationLogNotFoundToast } from '@utils/patientProfileCommunicationNavigation.util'
import { logger } from '@utils/safeLogger'

export type FaxSortField = 'date' | 'subject' | 'number' | null
export type FaxSortOrder = 'asc' | 'desc'

type FaxListRow = IFaxMessage & { _number: string; _date: string }

const sortCompare = (
  field: FaxSortField,
  order: FaxSortOrder,
): ((a: FaxListRow, b: FaxListRow) => number) => {
  const multiplier = order === 'asc' ? 1 : -1
  return (a, b) => {
    if (!field) return 0

    if (field === 'date') {
      const dateA = new Date(a.date_time_created_at ?? 0).getTime()
      const dateB = new Date(b.date_time_created_at ?? 0).getTime()
      return (dateA - dateB) * multiplier
    }

    if (field === 'subject') {
      const subA = (a.subject ?? '').toLowerCase()
      const subB = (b.subject ?? '').toLowerCase()
      return subA.localeCompare(subB) * multiplier
    }

    if (field === 'number') {
      return (
        a._number.toLowerCase().localeCompare(b._number.toLowerCase()) *
        multiplier
      )
    }

    return 0
  }
}

interface IConfirmModalState {
  show: boolean
  action: string
  mainText: string
  subText: string
  _selectedFaxes?: IFaxMessage[]
  _selectedBlockedUsers?: IBlockedUser[]
}

const PAGE_SIZE = 15

const matchesFaxSearch = (
  fax: IFaxMessage,
  query: string,
  isInboxView: boolean,
): boolean => {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const sub = (fax.subject ?? '').toLowerCase()
  const label = getFaxDisplayLabel(fax, isInboxView).toLowerCase()
  const num = getFaxCounterpartNumber(fax, isInboxView).toLowerCase()
  return sub.includes(q) || label.includes(q) || num.includes(q)
}

export type FaxPreviewHooks = {
  addContactInitialFaxNumber: string
  activeFolder: string
  blockModalContactId: string | null
  blockModalPhones: string[]
  canBlockUnblockSelected: boolean
  capabilities: FaxViewCapabilities
  showBlockAction: (fax: IFaxMessage) => boolean
  clinicName: string
  confirmModalState: IConfirmModalState | null
  currentPage: number
  handleRecoverSelected: () => void
  existingBlockedUsers: IBlockedUser[]
  faxBlockedUsers: IBlockedUser[]
  filteredBlockedUsers: IBlockedUser[]
  getDisplayLabel: (fax: IFaxMessage) => string
  getListDate: (fax: IFaxMessage) => string
  handleBlockUnblockSelected: () => void
  handleMoveToFolder: (folderId: string) => void
  handleMultipleSelectAction: (action: string, _faxes: IFaxMessage[]) => void
  handleBackFromDetail: () => void
  handleExitDetailAfterAction: (ids?: string[]) => void
  handlePageChange: (direction: 'prev' | 'next') => void
  handleRefresh: () => void
  handleRowClick: (fax: IFaxMessage) => void
  handleSelectAll: () => void
  handleSortFieldChange: (field: NonNullable<FaxSortField>) => void
  handleSortOrderChange: (order: FaxSortOrder) => void
  handleClearSort: () => void
  handleToggleReadSelected: () => void
  handleUnblockFax: (blockedUser: IBlockedUser) => void
  isAllPageSelected: boolean
  isFaxNumberBlocked: (phone: string | null | undefined) => boolean
  isUnblockMode: boolean
  isDetailView: boolean
  isInboxView: boolean
  isRowSelected: (faxId?: string) => boolean
  listMessages: FaxListRow[]
  listViewMode: FaxListViewMode
  onAddToContacts: (fax: IFaxMessage) => void
  onRetry: (fax: IFaxMessage) => void
  onConfirmActionButton: () => void
  onHideAddContactModal: () => void
  onHideBlockModal: () => void
  onHideConfirmModal: () => void
  onSelectTag: (id: string) => void
  openBlockModalForFax: (fax: IFaxMessage) => void
  pagedMessages: FaxListRow[]
  selectedFax: FaxListRow | null
  selectedFaxId: string | null
  selectedBlockedUsers: IBlockedUser[]
  selectedFaxes: IFaxMessage[]
  selectionFaxes: IFaxMessage[]
  selectAllChecked: boolean
  selectAllIndeterminate: boolean
  shouldShowAddContact: (fax: IFaxMessage) => boolean
  showAddContactModal: boolean
  showBlockModal: boolean
  sortField: FaxSortField
  sortOrder: FaxSortOrder
  tags: IFaxTag[]
  toggleBlockedUserSelected: (user: IBlockedUser) => void
  toggleRowSelected: (fax: IFaxMessage) => void
  totalPages: number
}

function useFaxPreview(): FaxPreviewHooks {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const { showToast } = useNotificationToast()
  const faxState = useAppSelector((state) => state.fax)
  const contacts = useAppSelector((state) => state.contacts.data)
  const selectedFaxId = useAppSelector((state) => state.fax.selectedFaxId)
  const activeFolder = useAppSelector((state) => state.fax.activeFolder)
  const faxFilters = useAppSelector((state) => state.fax.faxFilters)
  const tags = useAppSelector((state) => state.fax.tags)
  const folders = useAppSelector((state) => state.fax.folders)
  const blockedUsers = useAppSelector((state) => state.blockedUser.data)
  const clinicName =
    useAppSelector((state) => state.clinic.selectedClinic?.clinic_name) ||
    'Clinic'

  const listViewMode = useMemo(
    () => getFaxListViewMode(activeFolder),
    [activeFolder],
  )
  const capabilities = useMemo(
    () => getFaxViewCapabilities(activeFolder),
    [activeFolder],
  )
  const faxBlockedUsers = useMemo(
    () => getFaxBlockedUsers(blockedUsers),
    [blockedUsers],
  )
  const blockedPhones = useMemo(
    () => getBlockedPhoneSet(blockedUsers),
    [blockedUsers],
  )
  const isInboxView = useMemo(
    () => isFaxInboxView(activeFolder),
    [activeFolder],
  )

  const [selectedFaxes, setSelectedFaxes] = useState<IFaxMessage[]>([])
  const [selectedBlockedUsers, setSelectedBlockedUsers] = useState<
    IBlockedUser[]
  >([])
  const [confirmModalState, setConfirmModalState] =
    useState<IConfirmModalState | null>(null)
  const [sortField, setSortField] = useState<FaxSortField>(null)
  const [sortOrder, setSortOrder] = useState<FaxSortOrder>('desc')
  const [currentPage, setCurrentPage] = useState(1)
  const [showAddContactModal, setShowAddContactModal] = useState(false)
  const [addContactInitialFaxNumber, setAddContactInitialFaxNumber] =
    useState('')
  const [showBlockModal, setShowBlockModal] = useState(false)
  const [blockModalPhones, setBlockModalPhones] = useState<string[]>([])
  const [blockModalContactId, setBlockModalContactId] = useState<string | null>(
    null,
  )
  const prevActiveFolderRef = useRef(activeFolder)
  const appliedDeepLinkFaxIdRef = useRef<string | null>(null)
  const faxNotFoundToastShownRef = useRef(false)
  const [initialFaxFetchCompleted, setInitialFaxFetchCompleted] =
    useState(false)

  const fetchViewMessages = useCallback(() => {
    if (listViewMode === 'blocked') {
      dispatch(fetchAllBlockedUsers())
      return
    }
    dispatch(fetchAllFax(getFaxFetchView(activeFolder)))
    dispatch(fetchAllBlockedUsers())
  }, [activeFolder, dispatch, listViewMode])

  useEffect(() => {
    setInitialFaxFetchCompleted(false)
    if (listViewMode === 'blocked') {
      void dispatch(fetchAllBlockedUsers())
        .unwrap()
        .finally(() => {
          setInitialFaxFetchCompleted(true)
        })
      return
    }
    void dispatch(fetchAllFax(getFaxFetchView(activeFolder)))
      .unwrap()
      .finally(() => {
        setInitialFaxFetchCompleted(true)
      })
    dispatch(fetchAllBlockedUsers())
  }, [activeFolder, dispatch, listViewMode])

  useEffect(() => {
    if (prevActiveFolderRef.current === activeFolder) return
    prevActiveFolderRef.current = activeFolder
    setSelectedFaxes([])
    setSelectedBlockedUsers([])
    dispatch(setSelectedFaxId(null))
    setCurrentPage(1)
  }, [activeFolder, dispatch])

  const viewMessages = useMemo(() => {
    if (listViewMode === 'blocked') return []
    return getFaxMessagesForView(faxState.data, activeFolder, {
      blockedPhones,
    })
  }, [faxState.data, activeFolder, blockedPhones, listViewMode])

  const filteredMessages = useMemo(
    () =>
      viewMessages
        .filter((fax) =>
          matchesFaxSearch(fax, faxState.searchQuery, isInboxView),
        )
        .filter((fax) => matchesFaxFilters(fax, faxFilters)),
    [viewMessages, faxState.searchQuery, faxFilters, isInboxView],
  )

  const filteredBlockedUsers = useMemo(
    () =>
      faxBlockedUsers
        .filter((user) => matchesBlockedSearch(user, faxState.searchQuery))
        .filter((user) => matchesBlockedFilters(user, faxFilters)),
    [faxBlockedUsers, faxState.searchQuery, faxFilters],
  )

  const listMessages = useMemo(() => {
    const rows: FaxListRow[] = filteredMessages.map((r) => ({
      ...r,
      _number: getFaxCounterpartNumber(r, isInboxView),
      _date: r.date_time_created_at
        ? new Date(r.date_time_created_at).toLocaleDateString()
        : '',
    }))
    if (!sortField) {
      return rows.sort((a, b) => {
        const dateA = new Date(a.date_time_created_at ?? 0).getTime()
        const dateB = new Date(b.date_time_created_at ?? 0).getTime()
        return dateB - dateA
      })
    }
    return [...rows].sort(sortCompare(sortField, sortOrder))
  }, [filteredMessages, sortField, sortOrder, isInboxView])

  // Toolbar pagination only applies to default mailbox views; History and
  // Blocked paginate inside their DataGrid.
  const totalPages = Math.max(1, Math.ceil(listMessages.length / PAGE_SIZE))

  const pagedMessages = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE
    return listMessages.slice(start, start + PAGE_SIZE)
  }, [listMessages, currentPage])

  const selectedFax = useMemo(
    () => listMessages.find((f) => f.fax_message_id === selectedFaxId) ?? null,
    [listMessages, selectedFaxId],
  )

  const selectionFaxes = useMemo(
    () =>
      selectedFaxes.length > 0
        ? selectedFaxes
        : selectedFax
          ? [selectedFax]
          : [],
    [selectedFax, selectedFaxes],
  )

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages)
  }, [currentPage, totalPages])

  useEffect(() => {
    const routeState = location.state as {
      faxId?: string
      faxFolder?: 'inbox' | 'sent'
      previousPage?: unknown
    } | null
    const faxId = routeState?.faxId
    if (!faxId) {
      appliedDeepLinkFaxIdRef.current = null
      faxNotFoundToastShownRef.current = false
      return
    }
    if (appliedDeepLinkFaxIdRef.current === faxId) return

    const targetFolder = routeState?.faxFolder ?? 'inbox'
    const previousPage = routeState?.previousPage

    const clearDeepLinkState = (): void => {
      navigate(location.pathname, {
        replace: true,
        state: previousPage ? { previousPage } : null,
      })
    }

    if (activeFolder === 'compose' || activeFolder !== targetFolder) {
      dispatch(setActiveFolder(targetFolder))
      return
    }

    if (
      faxState.loading ||
      !initialFaxFetchCompleted ||
      listViewMode === 'blocked'
    ) {
      return
    }

    const fax = viewMessages.find((f) => f.fax_message_id === faxId)
    if (!fax) {
      if (!faxNotFoundToastShownRef.current) {
        faxNotFoundToastShownRef.current = true
        showCommunicationLogNotFoundToast(showToast, 'fax')
      }
      appliedDeepLinkFaxIdRef.current = faxId
      clearDeepLinkState()
      return
    }

    appliedDeepLinkFaxIdRef.current = faxId

    const idx = listMessages.findIndex((f) => f.fax_message_id === faxId)
    if (idx >= 0) {
      const targetPage = Math.floor(idx / PAGE_SIZE) + 1
      if (currentPage !== targetPage) {
        setCurrentPage(targetPage)
      }
    }

    dispatch(setSelectedFaxId(faxId))
    if (fax.fax_message_id && !fax.is_read) {
      dispatch(
        updateFax({
          fax_message_id: fax.fax_message_id,
          data: { is_read: true },
        }),
      )
    }

    clearDeepLinkState()
  }, [
    activeFolder,
    currentPage,
    dispatch,
    faxState.loading,
    initialFaxFetchCompleted,
    listMessages,
    listViewMode,
    location.pathname,
    location.state,
    navigate,
    showToast,
    viewMessages,
  ])

  useEffect(() => {
    if (selectedFaxId && !selectedFax) {
      dispatch(setSelectedFaxId(null))
    }
  }, [selectedFaxId, selectedFax, dispatch])

  const exitDetailView = useCallback(() => {
    dispatch(setSelectedFaxId(null))
  }, [dispatch])

  const handleBackFromDetail = exitDetailView

  const handleExitDetailAfterAction = useCallback(
    (ids?: string[]) => {
      if (ids?.length) {
        setSelectedFaxes((prev) =>
          prev.filter((f) => !ids.includes(f.fax_message_id ?? '')),
        )
      }
      exitDetailView()
    },
    [exitDetailView],
  )

  const isDetailView = Boolean(selectedFaxId)

  const handleRowClick = useCallback(
    (fax: IFaxMessage) => {
      if (!fax.fax_message_id) return
      dispatch(setSelectedFaxId(fax.fax_message_id))
      if (!fax.is_read) {
        dispatch(
          updateFax({
            fax_message_id: fax.fax_message_id,
            data: { is_read: true },
          }),
        )
      }
    },
    [dispatch],
  )

  const handleRefresh = useCallback(() => {
    fetchViewMessages()
  }, [fetchViewMessages])

  const onRetry = useCallback(
    async (fax: IFaxMessage) => {
      if (!fax.fax_message_id) return
      if (fax.status !== 'failed' || fax.direction !== 'Outgoing') return
      try {
        await dispatch(
          updateFax({
            fax_message_id: fax.fax_message_id,
            data: { intent: 'send' },
          }),
        ).unwrap()
        showToast(createFaxResendingToast())
      } catch {
        showToast(createFaxResendFailedToast())
      }
    },
    [dispatch, showToast],
  )

  const handlePageChange = useCallback(
    (direction: 'prev' | 'next') => {
      setCurrentPage((p) => {
        if (direction === 'prev') return Math.max(1, p - 1)
        return Math.min(totalPages, p + 1)
      })
    },
    [totalPages],
  )

  const toggleRowSelected = useCallback((fax: IFaxMessage) => {
    setSelectedFaxes((prev) => {
      const exists = prev.some((f) => f.fax_message_id === fax.fax_message_id)
      if (exists) {
        return prev.filter((f) => f.fax_message_id !== fax.fax_message_id)
      }
      return [...prev, fax]
    })
  }, [])

  const isRowSelected = useCallback(
    (faxId?: string) =>
      !!faxId && selectedFaxes.some((f) => f.fax_message_id === faxId),
    [selectedFaxes],
  )

  const isAllPageSelected = useMemo(
    () =>
      pagedMessages.length > 0 &&
      pagedMessages.every((f) =>
        selectedFaxes.some((s) => s.fax_message_id === f.fax_message_id),
      ),
    [pagedMessages, selectedFaxes],
  )

  // Toolbar select-all targets differ per view: History selects every displayed
  // fax row, Blocked selects every displayed blocked user, and default mailbox
  // views select the current page.
  const isAllSelected = useMemo(() => {
    if (listViewMode === 'blocked') {
      return (
        filteredBlockedUsers.length > 0 &&
        filteredBlockedUsers.every((u) =>
          selectedBlockedUsers.some(
            (s) => s.blocked_user_id === u.blocked_user_id,
          ),
        )
      )
    }
    if (listViewMode === 'history') {
      return (
        listMessages.length > 0 &&
        listMessages.every((f) =>
          selectedFaxes.some((s) => s.fax_message_id === f.fax_message_id),
        )
      )
    }
    return isAllPageSelected
  }, [
    listViewMode,
    filteredBlockedUsers,
    selectedBlockedUsers,
    listMessages,
    selectedFaxes,
    isAllPageSelected,
  ])

  const selectAllChecked = useMemo(() => {
    if (listViewMode === 'blocked') {
      return isAllSelected && filteredBlockedUsers.length > 0
    }
    if (listViewMode === 'history') {
      return isAllSelected && listMessages.length > 0
    }
    return isAllSelected && pagedMessages.length > 0
  }, [
    listViewMode,
    isAllSelected,
    filteredBlockedUsers.length,
    listMessages.length,
    pagedMessages.length,
  ])

  const selectAllIndeterminate = useMemo(() => {
    const hasSelection =
      listViewMode === 'blocked'
        ? selectedBlockedUsers.length > 0
        : selectedFaxes.length > 0
    return !isAllSelected && hasSelection
  }, [
    listViewMode,
    isAllSelected,
    selectedBlockedUsers.length,
    selectedFaxes.length,
  ])

  const toggleBlockedUserSelected = useCallback((user: IBlockedUser) => {
    setSelectedBlockedUsers((prev) => {
      const exists = prev.some(
        (u) => u.blocked_user_id === user.blocked_user_id,
      )
      if (exists) {
        return prev.filter((u) => u.blocked_user_id !== user.blocked_user_id)
      }
      return [...prev, user]
    })
  }, [])

  const handleToggleReadSelected = useCallback(() => {
    if (selectionFaxes.length === 0) return
    const markRead = selectionFaxes.some((f) => !f.is_read)
    dispatch(
      bulkUpdateFax({
        ids: selectionFaxes.map((f) => f.fax_message_id!),
        data: { is_read: markRead },
      }),
    )
  }, [dispatch, selectionFaxes])

  const handleRecoverSelected = useCallback(() => {
    if (selectionFaxes.length === 0) return
    const ids = selectionFaxes.map((f) => f.fax_message_id!)
    dispatch(
      bulkUpdateFax({
        ids,
        data: { is_hidden: false, intent: 'restore' },
      }),
    )
    setSelectedFaxes([])
    if (selectedFaxId) {
      handleExitDetailAfterAction(ids)
    }
    showToast(createFaxRestoredToInboxToast())
  }, [
    dispatch,
    handleExitDetailAfterAction,
    selectedFaxId,
    selectionFaxes,
    showToast,
  ])

  const onAddToContacts = useCallback(
    (fax: IFaxMessage) => {
      setAddContactInitialFaxNumber(getFaxCounterpartNumber(fax, isInboxView))
      setShowAddContactModal(true)
    },
    [isInboxView],
  )

  const onHideAddContactModal = useCallback(() => {
    setShowAddContactModal(false)
    setAddContactInitialFaxNumber('')
  }, [])

  const handleMoveToFolder = useCallback(
    (folderId: string) => {
      const ids = selectionFaxes.map((f) => f.fax_message_id!).filter(Boolean)
      if (ids.length === 0) return
      const folderName =
        folders.find((folder) => folder.fax_folder_id === folderId)
          ?.folder_name ?? 'folder'
      dispatch(
        bulkUpdateFax({
          ids,
          data: { fax_folder_id: folderId, intent: 'move_to_folder' },
        }),
      )
      if (selectedFaxId) {
        handleExitDetailAfterAction(ids)
      }
      showToast(createFaxMovedToFolderToast(folderName))
    },
    [
      dispatch,
      folders,
      handleExitDetailAfterAction,
      selectedFaxId,
      selectionFaxes,
      showToast,
    ],
  )

  const handleSortFieldChange = useCallback(
    (field: NonNullable<FaxSortField>): void => {
      setSortField(field)
      setSortOrder((prev) => prev || 'desc')
    },
    [],
  )

  const handleSortOrderChange = useCallback((order: FaxSortOrder): void => {
    setSortOrder(order)
    setSortField((prev) => prev ?? 'date')
  }, [])

  const handleClearSort = useCallback((): void => {
    setSortField(null)
    setSortOrder('desc')
  }, [])

  const handleSelectAll = useCallback((): void => {
    if (listViewMode === 'blocked') {
      setSelectedBlockedUsers(isAllSelected ? [] : filteredBlockedUsers)
      return
    }
    if (listViewMode === 'history') {
      setSelectedFaxes(isAllSelected ? [] : listMessages)
      return
    }
    setSelectedFaxes(isAllPageSelected ? [] : pagedMessages)
  }, [
    listViewMode,
    isAllSelected,
    isAllPageSelected,
    filteredBlockedUsers,
    listMessages,
    pagedMessages,
  ])

  const onHideConfirmModal = useCallback((): void => {
    setConfirmModalState(null)
  }, [])

  const onSelectTag = useCallback(
    (id: string): void => {
      if (selectionFaxes.length === 0) return
      dispatch(
        bulkUpdateFax({
          ids: selectionFaxes.map((fax) => fax.fax_message_id!),
          data: { fax_tag_id: id },
        }),
      )
    },
    [dispatch, selectionFaxes],
  )

  const isFaxNumberBlocked = useCallback(
    (phone: string | null | undefined) => {
      if (!phone?.trim() || blockedPhones.size === 0) return false
      return blockedPhones.has(normalizePhoneNumber(phone))
    },
    [blockedPhones],
  )

  const showBlockAction = useCallback(
    (fax: IFaxMessage) => {
      if (listViewMode === 'blocked' || fax.status === 'draft') return false
      const phone = getFaxCounterpartNumber(fax, isInboxView)
      return !!phone?.trim() && !isFaxNumberBlocked(phone)
    },
    [isFaxNumberBlocked, isInboxView, listViewMode],
  )

  const openBlockModal = useCallback(
    (phones: string[], contactId: string | null) => {
      if (phones.length === 0) return
      setBlockModalPhones(phones)
      setBlockModalContactId(contactId)
      setShowBlockModal(true)
    },
    [],
  )

  const openBlockModalForFax = useCallback(
    (fax: IFaxMessage) => {
      const phone = getFaxCounterpartNumber(fax, isInboxView)
      if (!phone?.trim()) return
      openBlockModal([phone], fax.contact_id ?? null)
    },
    [isInboxView, openBlockModal],
  )

  const onHideBlockModal = useCallback(() => {
    setShowBlockModal(false)
    setBlockModalPhones([])
    setBlockModalContactId(null)
  }, [])

  const findFaxBlockedUserByPhone = useCallback(
    (phone: string): IBlockedUser | undefined => {
      const normalized = normalizePhoneNumber(phone)
      if (!normalized) return undefined
      return faxBlockedUsers.find(
        (user) => normalizePhoneNumber(user.phone_number ?? '') === normalized,
      )
    },
    [faxBlockedUsers],
  )

  const getBlockedUsersFromFaxSelection = useCallback((): IBlockedUser[] => {
    const users = new Map<string, IBlockedUser>()
    selectionFaxes.forEach((fax) => {
      const phone = getFaxCounterpartNumber(fax, isInboxView)
      if (!phone?.trim() || !isFaxNumberBlocked(phone)) return
      const user = findFaxBlockedUserByPhone(phone)
      if (user?.blocked_user_id) {
        users.set(user.blocked_user_id, user)
      }
    })
    return Array.from(users.values())
  }, [
    findFaxBlockedUserByPhone,
    isFaxNumberBlocked,
    isInboxView,
    selectionFaxes,
  ])

  const getUniqueUnblockedPhonesFromSelection = useCallback((): string[] => {
    const phones = new Set<string>()
    selectionFaxes.forEach((fax) => {
      const phone = getFaxCounterpartNumber(fax, isInboxView)
      if (phone?.trim() && !isFaxNumberBlocked(phone)) {
        phones.add(phone)
      }
    })
    return Array.from(phones)
  }, [isFaxNumberBlocked, isInboxView, selectionFaxes])

  const hasBlockedInSelection = useMemo(
    () => getBlockedUsersFromFaxSelection().length > 0,
    [getBlockedUsersFromFaxSelection],
  )

  const isUnblockMode = listViewMode === 'blocked' || hasBlockedInSelection

  const canBlockUnblockSelected = useMemo(() => {
    if (listViewMode === 'blocked') {
      return selectedBlockedUsers.length > 0
    }
    if (selectionFaxes.length === 0) return false
    if (isUnblockMode) {
      return getBlockedUsersFromFaxSelection().length > 0
    }
    return getUniqueUnblockedPhonesFromSelection().length > 0
  }, [
    getBlockedUsersFromFaxSelection,
    getUniqueUnblockedPhonesFromSelection,
    isUnblockMode,
    listViewMode,
    selectedBlockedUsers.length,
    selectionFaxes.length,
  ])

  const unblockFaxBlockedUser = useCallback(
    async (blockedUser: IBlockedUser): Promise<void> => {
      if (!blockedUser.blocked_user_id) return
      const features = Array.isArray(blockedUser.blocked_features)
        ? blockedUser.blocked_features
        : []
      if (features.length === 1 && features.includes('fax')) {
        await dispatch(unblockUser(blockedUser.blocked_user_id)).unwrap()
      } else {
        await dispatch(
          updateBlockedUser({
            blocked_user_id: blockedUser.blocked_user_id,
            data: {
              blocked_features: features.filter((f) => f !== 'fax'),
            },
          }),
        ).unwrap()
      }
    },
    [dispatch],
  )

  const handleBlockUnblockSelected = useCallback(() => {
    if (!canBlockUnblockSelected) return

    if (isUnblockMode) {
      const usersToUnblock =
        listViewMode === 'blocked'
          ? selectedBlockedUsers
          : getBlockedUsersFromFaxSelection()
      if (usersToUnblock.length === 0) return
      const count = usersToUnblock.length
      setConfirmModalState({
        show: true,
        action: 'unblock_fax',
        mainText: `Unblock ${count} fax number${count === 1 ? '' : 's'}?`,
        subText:
          'After unblocking, they will be able to send and receive faxes again.',
        _selectedBlockedUsers: usersToUnblock,
      })
      return
    }

    const phones = getUniqueUnblockedPhonesFromSelection()
    const contactId =
      selectionFaxes.find((f) => f.contact_id)?.contact_id ?? null
    openBlockModal(phones, contactId)
  }, [
    canBlockUnblockSelected,
    getBlockedUsersFromFaxSelection,
    getUniqueUnblockedPhonesFromSelection,
    isUnblockMode,
    listViewMode,
    openBlockModal,
    selectedBlockedUsers,
    selectionFaxes,
  ])

  const handleUnblockFax = useCallback(
    async (blockedUser: IBlockedUser) => {
      try {
        await unblockFaxBlockedUser(blockedUser)
        await dispatch(fetchAllBlockedUsers())
        showToast(
          createSuccessToast('Unblocked', 'Fax number has been unblocked.'),
        )
      } catch (e) {
        console.error(e)
        showToast(createFailedToast('Unblock', 'Could not unblock fax number.'))
      }
    },
    [dispatch, showToast, unblockFaxBlockedUser],
  )

  const onConfirmActionButton = useCallback((): void => {
    if (!confirmModalState) return

    const {
      action,
      _selectedFaxes = [],
      _selectedBlockedUsers = [],
    } = confirmModalState

    if (action === 'unblock_fax') {
      if (_selectedBlockedUsers.length === 0) return
      void (async () => {
        try {
          for (const user of _selectedBlockedUsers) {
            await unblockFaxBlockedUser(user)
          }
          await dispatch(fetchAllBlockedUsers())
          setSelectedBlockedUsers([])
          const count = _selectedBlockedUsers.length
          showToast(
            createSuccessToast(
              'Unblocked',
              `${count} fax number${count === 1 ? '' : 's'} ha${count === 1 ? 's' : 've'} been unblocked.`,
            ),
          )
          if (selectedFaxId) {
            handleExitDetailAfterAction()
          }
        } catch (e) {
          console.error(e)
          showToast(
            createFailedToast('Unblock', 'Could not unblock fax number(s).'),
          )
        } finally {
          setConfirmModalState(null)
        }
      })()
      return
    }

    if (_selectedFaxes.length === 0) return

    const affectedIds = _selectedFaxes
      .map((fax) => fax.fax_message_id!)
      .filter(Boolean)
    let updateData: Partial<IFaxMessage> = {}
    switch (action) {
      case 'view':
        if (_selectedFaxes.length === 1 && _selectedFaxes[0].fax_message_id) {
          dispatch(setSelectedFaxId(_selectedFaxes[0].fax_message_id))
        }
        updateData = { is_read: true }
        break
      case 'unarchive':
        updateData = { intent: 'unarchive', is_archived: false }
        break
      case 'archive':
        updateData = { is_archived: true, intent: 'archive' }
        break
      case 'delete':
        updateData = { is_hidden: true, intent: 'move_to_trash' }
        break
    }

    if (_selectedFaxes.length === 1) {
      if (action === 'permanently_delete') {
        dispatch(deleteFax(_selectedFaxes[0].fax_message_id!))
      } else {
        dispatch(
          updateFax({
            fax_message_id: _selectedFaxes[0].fax_message_id!,
            data: updateData,
          }),
        )
      }
    } else if (action === 'permanently_delete') {
      dispatch(bulkDeleteFax(_selectedFaxes.map((fax) => fax.fax_message_id!)))
    } else {
      dispatch(
        bulkUpdateFax({
          ids: _selectedFaxes.map((fax) => fax.fax_message_id!),
          data: updateData,
        }),
      )
    }

    if (action === 'delete') {
      showToast(createFaxMovedToTrashToast())
    } else if (action === 'unarchive') {
      showToast(createFaxRestoredToInboxToast())
    }

    const shouldExitDetail = [
      'delete',
      'permanently_delete',
      'archive',
      'unarchive',
    ].includes(action)
    if (shouldExitDetail) {
      handleExitDetailAfterAction(affectedIds)
    }
    setConfirmModalState(null)
  }, [
    confirmModalState,
    dispatch,
    handleExitDetailAfterAction,
    selectedFaxId,
    showToast,
    unblockFaxBlockedUser,
  ])

  const handleMultipleSelectAction = useCallback(
    (action: string, _faxes: IFaxMessage[]): void => {
      if (action === 'read') {
        dispatch(
          bulkUpdateFax({
            ids: _faxes.map((fax) => fax.fax_message_id!),
            data: { is_read: true },
          }),
        )
        return
      }
      if (action === 'unread') {
        dispatch(
          bulkUpdateFax({
            ids: _faxes.map((fax) => fax.fax_message_id!),
            data: { is_read: false },
          }),
        )
        return
      }
      if (action === 'archive') {
        dispatch(
          bulkUpdateFax({
            ids: _faxes.map((fax) => fax.fax_message_id!),
            data: { is_archived: true, intent: 'archive' },
          }),
        )
        if (selectedFaxId) {
          handleExitDetailAfterAction(
            _faxes.map((fax) => fax.fax_message_id!).filter(Boolean),
          )
        }
        return
      }
      if (action === 'unarchive') {
        dispatch(
          bulkUpdateFax({
            ids: _faxes.map((fax) => fax.fax_message_id!),
            data: { intent: 'unarchive', is_archived: false },
          }),
        )
        if (selectedFaxId) {
          handleExitDetailAfterAction(
            _faxes.map((fax) => fax.fax_message_id!).filter(Boolean),
          )
        }
        showToast(createFaxRestoredToInboxToast())
        return
      }
      if (action.includes('delete')) {
        let mainText = ''
        let subText = ''
        switch (action) {
          case 'delete':
            mainText = `Delete ${_faxes.length} fax messages?`
            subText = 'These fax messages will be moved to the trash folder.'
            break
          case 'permanently_delete':
            mainText = `Permanently delete ${_faxes.length} fax messages?`
            subText =
              'These fax messages will be deleted from the list and cannot be recovered.'
            break
        }
        setConfirmModalState({
          show: true,
          action,
          mainText,
          subText,
          _selectedFaxes: _faxes,
        })
        return
      }
      if (action !== 'download') return

      const sanitizeFileName = (name: string): string =>
        name.replace(/[^a-z0-9._-]/gi, '_').trim() || 'document'

      const downloadFileAsPdf = async (
        fileUrl: string,
        fileName: string,
      ): Promise<void> => {
        try {
          const response = await downloadFile(fileUrl)
          if (response.statusCode !== 200 || !response.data) {
            throw new Error(response.message || 'Download failed')
          }
          const blob = response.data
          const url = URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = fileName.endsWith('.pdf')
            ? fileName
            : `${fileName}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          URL.revokeObjectURL(url)
        } catch (error) {
          logger.error('Error downloading fax document:', error)
        }
      }

      let delay = 0
      const delayMs = 150
      _faxes.forEach((fax, faxIndex) => {
        const baseName =
          sanitizeFileName(String(fax.subject || fax.fax_message_id || 'fax')) +
          (faxIndex > 0 ? `_${faxIndex}` : '')
        const documents: IFaxDocument[] = fax.documents ?? []
        documents.forEach((doc, docIndex) => {
          const fileName =
            documents.length === 1
              ? `${baseName}.pdf`
              : `${baseName}_page_${docIndex + 1}.pdf`
          const displayName =
            doc.file_name && doc.file_name.trim()
              ? sanitizeFileName(doc.file_name)
              : fileName
          setTimeout(() => downloadFileAsPdf(doc.file_url, displayName), delay)
          delay += delayMs
        })
      })
    },
    [dispatch, handleExitDetailAfterAction, selectedFaxId, showToast],
  )

  const shouldShowAddContact = useCallback(
    (fax: IFaxMessage): boolean => {
      if (!capabilities.showAddContact) return false
      if (fax.contact_id || fax.contact?.contact_id) return false
      const num = getFaxCounterpartNumber(fax, isInboxView)
      if (!num?.trim()) return false
      const normalized = normalizePhoneNumber(num)
      if (!normalized) return false
      return !contacts.some((c) => {
        const candidates = [
          c.fax_number,
          c.cellphone,
          c.home_phone,
          c.work_phone,
        ]
        return candidates.some(
          (n) => n && normalizePhoneNumber(n) === normalized,
        )
      })
    },
    [capabilities.showAddContact, contacts, isInboxView],
  )

  return {
    addContactInitialFaxNumber,
    activeFolder,
    blockModalContactId,
    blockModalPhones,
    canBlockUnblockSelected,
    capabilities,
    showBlockAction,
    clinicName,
    confirmModalState,
    currentPage,
    handleRecoverSelected,
    existingBlockedUsers: blockedUsers,
    faxBlockedUsers,
    filteredBlockedUsers,
    getDisplayLabel: (fax: IFaxMessage) => getFaxDisplayLabel(fax, isInboxView),
    getListDate: (fax: IFaxMessage) =>
      formatFaxListDate(fax.date_time_created_at),
    handleBlockUnblockSelected,
    handleBackFromDetail,
    handleExitDetailAfterAction,
    handleMoveToFolder,
    handleMultipleSelectAction,
    handlePageChange,
    handleRefresh,
    handleRowClick,
    handleSelectAll,
    handleSortFieldChange,
    handleSortOrderChange,
    handleClearSort,
    handleToggleReadSelected,
    handleUnblockFax,
    isAllPageSelected,
    isDetailView,
    isFaxNumberBlocked,
    isInboxView,
    isRowSelected,
    isUnblockMode,
    listMessages,
    listViewMode,
    onAddToContacts,
    onRetry,
    onConfirmActionButton,
    onHideAddContactModal,
    onHideBlockModal,
    onHideConfirmModal,
    onSelectTag,
    openBlockModalForFax,
    pagedMessages,
    selectedFax,
    selectedFaxId,
    selectedBlockedUsers,
    selectedFaxes,
    selectionFaxes,
    selectAllChecked,
    selectAllIndeterminate,
    showAddContactModal,
    showBlockModal,
    shouldShowAddContact,
    sortField,
    sortOrder,
    tags,
    toggleBlockedUserSelected,
    toggleRowSelected,
    totalPages,
  }
}

export default useFaxPreview
